import { normalizeKind, worldSizeToScreen, worldToScreen } from "./camera";
import type { Camera, GameSnapshot, SnapshotEntity } from "./types";

export type RenderAudioEvents = {
  visibleDamage: boolean;
  localShot: boolean;
  localHeartPickup: boolean;
  localLevelUp: boolean;
};

type LocalPlayerState = {
  key: string;
  position: {
    x: number;
    y: number;
  };
  radius: number;
  hp: number | null;
  xp: number | null;
  level: number | null;
};

type TrackedHeart = {
  key: string;
  position: {
    x: number;
    y: number;
  };
  radius: number;
  visible: boolean;
};

type TrackedEntity = {
  key: string;
  kind: string;
  owner: number | string | null;
  position: {
    x: number;
    y: number;
  };
  radius: number;
  hp: number | null;
  visible: boolean;
};

const LOCAL_SHOT_IMPACT_RANGE = 980;
const LOCAL_SHOT_IMPACT_MARGIN = 120;

export class RenderAudioEventTracker {
  private ready = false;
  private previousLocalProjectiles = new Set<string>();
  private previousHearts = new Map<string, TrackedHeart>();
  private previousEntities = new Map<string, TrackedEntity>();
  private previousLocalPlayer: LocalPlayerState | null = null;

  public collect(
    snapshot: GameSnapshot,
    camera: Camera,
    width: number,
    height: number
  ): RenderAudioEvents {
    const events = createEmptyRenderAudioEvents();
    const entities = snapshot.entities as SnapshotEntity[];
    const localId = extractEntityId(snapshot.local_player_id ?? snapshot.localPlayerId);
    const localPlayer = findLocalPlayer(snapshot, localId);
    const localPlayerState = localPlayer ? createLocalPlayerState(localPlayer) : null;
    const localVisible = localPlayer
      ? isEntityVisible(localPlayer, camera, width, height)
      : false;
    const currentLocalProjectiles = new Set<string>();
    const currentHearts = new Map<string, TrackedHeart>();
    const currentEntities = new Map<string, TrackedEntity>();

    for (const entity of entities) {
      const kind = normalizeKind(entity.kind);
      const visible = isEntityVisible(entity, camera, width, height);
      const key = entityKey(entity);
      const owner = extractEntityId(entity.owner);

      currentEntities.set(key, {
        key,
        kind,
        owner,
        position: entity.position,
        radius: entity.radius,
        hp: normalizeNumber(entity.hp),
        visible
      });

      if (kind === "projectile" && localId !== null && owner === localId) {
        currentLocalProjectiles.add(key);

        if (this.ready && visible && !this.previousLocalProjectiles.has(key)) {
          events.localShot = true;
        }
      }

      if (kind === "heart") {
        currentHearts.set(key, {
          key,
          position: entity.position,
          radius: entity.radius,
          visible
        });
      }
    }

    if (
      this.ready &&
      !events.localShot &&
      localId !== null &&
      localPlayerState &&
      localVisible &&
      this.didVisibleEnemyHpDropNearLocalPlayer(currentEntities, localPlayerState, localId)
    ) {
      events.localShot = true;
    }

    if (this.ready && localPlayer && localPlayerState && this.previousLocalPlayer) {
      const xpIncreased = didNumberIncrease(localPlayerState.xp, this.previousLocalPlayer.xp);
      const hpIncreased = didNumberIncrease(localPlayerState.hp, this.previousLocalPlayer.hp);
      const levelIncreased = didNumberIncrease(
        localPlayerState.level,
        this.previousLocalPlayer.level
      );

      events.localLevelUp = localVisible && levelIncreased;
      events.localHeartPickup =
        localVisible &&
        (xpIncreased || hpIncreased || levelIncreased) &&
        this.didNearbyVisibleHeartDisappear(
          currentHearts,
          localPlayerState,
          this.previousLocalPlayer
        );
    }

    this.previousLocalProjectiles = currentLocalProjectiles;
    this.previousHearts = currentHearts;
    this.previousEntities = currentEntities;
    this.previousLocalPlayer = localPlayerState;
    this.ready = true;

    return events;
  }

  public reset(): void {
    this.ready = false;
    this.previousLocalProjectiles.clear();
    this.previousHearts.clear();
    this.previousEntities.clear();
    this.previousLocalPlayer = null;
  }

  private didNearbyVisibleHeartDisappear(
    currentHearts: Map<string, TrackedHeart>,
    localPlayer: LocalPlayerState,
    previousLocalPlayer: LocalPlayerState
  ): boolean {
    for (const heart of this.previousHearts.values()) {
      if (!heart.visible || currentHearts.has(heart.key)) {
        continue;
      }

      const pickupDistance =
        Math.max(localPlayer.radius, previousLocalPlayer.radius) + heart.radius + 42;
      const currentDistance = distanceBetween(heart.position, localPlayer.position);
      const previousDistance = distanceBetween(heart.position, previousLocalPlayer.position);

      if (Math.min(currentDistance, previousDistance) <= pickupDistance) {
        return true;
      }
    }

    return false;
  }

  private didVisibleEnemyHpDropNearLocalPlayer(
    currentEntities: Map<string, TrackedEntity>,
    localPlayer: LocalPlayerState,
    localId: number | string
  ): boolean {
    for (const current of currentEntities.values()) {
      if (!current.visible || current.hp === null || current.owner === localId) {
        continue;
      }

      if (!isDamageReceiverKind(current.kind)) {
        continue;
      }

      const previous = this.previousEntities.get(current.key);

      if (!previous || previous.hp === null || current.hp >= previous.hp) {
        continue;
      }

      const currentDistance = distanceBetween(current.position, localPlayer.position);
      const previousDistance = distanceBetween(previous.position, localPlayer.position);
      const maxDistance =
        LOCAL_SHOT_IMPACT_RANGE +
        localPlayer.radius +
        Math.max(current.radius, previous.radius) +
        LOCAL_SHOT_IMPACT_MARGIN;

      if (Math.min(currentDistance, previousDistance) <= maxDistance) {
        return true;
      }
    }

    return false;
  }
}

export function createEmptyRenderAudioEvents(): RenderAudioEvents {
  return {
    visibleDamage: false,
    localShot: false,
    localHeartPickup: false,
    localLevelUp: false
  };
}

function findLocalPlayer(
  snapshot: GameSnapshot,
  localId: number | string | null
): SnapshotEntity | null {
  if (localId === null) {
    return (
      snapshot.entities.find((entity) => {
        const kind = normalizeKind(entity.kind);
        return kind === "player" || kind === "bot";
      }) ?? null
    );
  }

  return (
    snapshot.entities.find((entity) => {
      const kind = normalizeKind(entity.kind);
      return (kind === "player" || kind === "bot") && extractEntityId(entity.owner) === localId;
    }) ?? null
  );
}

function createLocalPlayerState(entity: SnapshotEntity): LocalPlayerState {
  return {
    key: entityKey(entity),
    position: entity.position,
    radius: entity.radius,
    hp: normalizeNumber(entity.hp),
    xp: normalizeNumber(entity.xp),
    level: normalizeNumber(entity.level)
  };
}

function isEntityVisible(
  entity: SnapshotEntity,
  camera: Camera,
  width: number,
  height: number
): boolean {
  const screen = worldToScreen(entity.position.x, entity.position.y, camera, width, height);
  const radius = worldSizeToScreen(entity.radius, camera);
  const margin = 100;

  return !(
    screen.x + radius < -margin ||
    screen.x - radius > width + margin ||
    screen.y + radius < -margin ||
    screen.y - radius > height + margin
  );
}

function isDamageReceiverKind(kind: string): boolean {
  return kind === "player" || kind === "bot" || kind === "zombie";
}

function didNumberIncrease(current: number | null, previous: number | null): boolean {
  return current !== null && previous !== null && current > previous;
}

function normalizeNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function entityKey(entity: SnapshotEntity): string {
  if (typeof entity.id === "number" || typeof entity.id === "string") {
    return `${entity.kind}:${entity.id}`;
  }

  if (entity.id && typeof entity.id === "object" && "0" in entity.id) {
    const nested = (entity.id as { 0?: unknown })[0];

    if (typeof nested === "number" || typeof nested === "string") {
      return `${entity.kind}:${nested}`;
    }
  }

  return `${entity.kind}:${entity.position.x}:${entity.position.y}:${entity.radius}`;
}

function extractEntityId(value: unknown): number | string | null {
  if (typeof value === "number" || typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "0" in value) {
    const nested = (value as { 0?: unknown })[0];

    if (typeof nested === "number" || typeof nested === "string") {
      return nested;
    }
  }

  return null;
}

function distanceBetween(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}